import { Box, BoxProps, useTheme } from "@mui/material";
import { capitalize } from "@simplyk/common";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { getDate } from "@/forms-design-system/FormDate/helper";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { mergeSx } from "@/helpers/mergeSx";
import { useMediaQuery } from "@/hooks/useMediaQuery";

type FormDateCalendarSquareProps = BoxProps & {
  date: Date | number;
};

export const FormDateCalendarSquare = ({ date, sx, ...rest }: FormDateCalendarSquareProps) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();
  const { isoLocale } = useLocaleContext();

  const luxonDate = getDate(date);
  const month = luxonDate.toLocaleString({ month: "short" }, { locale: isoLocale });
  const day = luxonDate.toLocaleString({ day: "numeric" }, { locale: isoLocale });

  const styles = mergeSx(
    {
      width: { xs: "38px", md: "46px" },
      height: { xs: "38px", md: "46px" },
      backgroundColor: theme.palette.formsComponents.date.calendarSquareBackgroundColor,
      border: `1px solid ${theme.palette.formsComponents.date.calendarSquareBorderColor}`,
      borderRadius: theme.radius(1),
      overflow: "hidden",
    },
    sx
  );

  return (
    <Box sx={styles} {...rest}>
      <FormTypography
        height={{ xs: "19px", md: "21px" }} // Leave space for border
        color={theme.palette.formsComponents.date.calendarSquareTextColor}
        variant={isSmallScreen ? "body2" : "body1"}
        textAlign="center"
        sx={{
          lineHeight: { xs: "19px", md: "21px" },
          backgroundColor: theme.palette.formsComponents.date.calendarSquareMonthBackgroundColor,
          borderBlockEnd: `1px solid ${theme.palette.formsComponents.date.calendarSquareBorderColor}`,
        }}
      >
        {capitalize(month)}
      </FormTypography>
      <FormTypography
        height={{ xs: "18px", md: "24px" }}
        color={theme.palette.formsComponents.date.calendarSquareTextColor}
        variant={isSmallScreen ? "subtitle2" : "subtitle1"}
        textAlign="center"
        sx={{
          lineHeight: { xs: "18px", md: "24px" },
        }}
      >
        {day}
      </FormTypography>
    </Box>
  );
};
